import { useCallback, useEffect, useRef, useState } from 'react';
import type { SendResult } from '@/hooks/use-agent-chat.hook';

type ReviewContext = {
  organizationId: string;
  agentId: string;
  conversationId: string;
  superseded?: boolean;
};
export type AcceptedPlayReviewRequest = ReviewContext &
  (
    | { kind?: 'accepted'; proposalId: string; playId?: string }
    | {
        kind: 'saved';
        saveEventId: string;
        playId: string;
        version: number;
        ready: boolean;
        /** A newer save is still in flight in the editor. */
        pending?: boolean;
        interrupted?: boolean;
        recover?: boolean;
      }
  );
export const playReviewKey = (request: AcceptedPlayReviewRequest) =>
  request.kind === 'saved'
    ? `saved:${request.saveEventId}`
    : request.proposalId;
export type AcceptedPlayReview = AcceptedPlayReviewRequest & {
  /** `offered`: a manual canvas save, checked only when the user asks. */
  status:
    | 'offered'
    | 'queued'
    | 'checking'
    | 'failed'
    | 'done'
    | 'cancelled'
    | 'superseded';
  retryable: boolean;
  errorCode?: string;
  /** The user asked for this offered save's check (Check this play): it is
   * ready from then on, whatever the inspector state, once no save is in
   * flight. A newer save carries the request over to its own row. */
  asked?: boolean;
};
export const NO_ACCEPTED_REVIEWS: readonly AcceptedPlayReviewRequest[] = [];

/** A low-priority, typed check owned by a persistent Agent chat. Accept is
 * already saved; this queue neither grants permission nor replays that write.
 * Only an Accept starts a check by itself. A manual canvas save is offered and
 * runs when the user asks (`retry`, or the editor's Retry check, which sends a
 * `recover` save), so no save starts a charged turn on its own. */
export function useAcceptedPlayReview({
  requests,
  ready,
  historyFailed,
  send,
}: {
  requests: readonly AcceptedPlayReviewRequest[];
  ready: boolean;
  historyFailed: boolean;
  send: (request: AcceptedPlayReviewRequest) => Promise<SendResult>;
}) {
  const [reviews, setReviews] = useState<AcceptedPlayReview[]>([]);
  const rows = useRef(reviews);
  const alive = useRef(true);
  const active = useRef<object | null>(null);
  const seen = useRef(new Set<string>());
  const latest = useRef({ ready, send });
  latest.current = { ready, send };
  const commit = useCallback((next: AcceptedPlayReview[]) => {
    rows.current = next;
    setReviews(next);
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      active.current = null;
    };
  }, []);
  useEffect(() => {
    let next = rows.current;
    for (const request of requests) {
      const key = playReviewKey(request);
      const previous = next.find((row) => playReviewKey(row) === key);
      if (previous) {
        if (request.superseded && previous.status !== 'done') {
          next = next.map((row) =>
            row === previous
              ? {
                  ...row,
                  superseded: true,
                  status:
                    row.status === 'checking'
                      ? 'checking'
                      : ('superseded' as const),
                  retryable: false,
                }
              : row
          );
          continue;
        }
        if (
          request.kind === 'saved' &&
          request.interrupted &&
          ['offered', 'queued', 'failed'].includes(previous.status)
        ) {
          next = next.map((row) =>
            row === previous
              ? {
                  ...row,
                  ...request,
                  status: 'failed' as const,
                  retryable: false,
                  errorCode: 'saved_review_interrupted',
                }
              : row
          );
          continue;
        }
        if (
          request.kind === 'saved' &&
          request.recover &&
          (previous.errorCode === 'saved_review_interrupted' ||
            previous.status === 'offered')
        ) {
          next = next.map((row) =>
            row === previous
              ? {
                  ...row,
                  ...request,
                  status: 'queued' as const,
                  asked: true,
                  retryable: true,
                  errorCode: undefined,
                }
              : row
          );
          continue;
        }
        // A benign newer version may replace a pending descriptor, but never
        // restarts an attempted, completed or explicitly stopped check. A
        // check the user asked for stays ready: the editor's last request
        // (ready: false while the inspector was open) is never republished
        // once the editor is gone. A save still in flight holds it back.
        const asked = previous.asked === true;
        const readyNow =
          request.kind === 'saved' &&
          (request.ready || (asked && !request.pending));
        if (
          ['offered', 'queued', 'checking', 'failed', 'cancelled'].includes(
            previous.status
          ) &&
          request.kind === 'saved' &&
          previous.kind === 'saved' &&
          (request.version !== previous.version ||
            readyNow !== previous.ready ||
            Boolean(request.pending) !== Boolean(previous.pending))
        ) {
          next = next.map((row) =>
            row === previous
              ? {
                  ...row,
                  ...request,
                  ready: readyNow,
                  retryable:
                    row.retryable || row.errorCode === 'saved_review_stale',
                }
              : row
          );
        }
        continue;
      }
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      // A check the user asked for that is still waiting moves to the newer
      // save, so it runs for the latest version instead of being dropped.
      const asked =
        request.kind === 'saved' &&
        !request.superseded &&
        !request.interrupted &&
        rows.current.some(
          (row) =>
            row.kind === 'saved' &&
            row.asked === true &&
            row.status === 'queued' &&
            row.playId === request.playId &&
            row.agentId === request.agentId
        );
      if (request.kind === 'saved') {
        next = next.map((row) =>
          row.playId === request.playId &&
          row.agentId === request.agentId &&
          row.status !== 'checking' &&
          row.status !== 'done'
            ? { ...row, status: 'superseded' as const, retryable: false }
            : row
        );
      }
      next = [
        ...next,
        {
          ...request,
          ...(request.kind === 'saved' && request.recover
            ? { asked: true }
            : {}),
          ...(asked && request.kind === 'saved'
            ? { asked: true, ready: request.ready || !request.pending }
            : {}),
          status: request.superseded
            ? 'superseded'
            : request.kind === 'saved' && request.interrupted
              ? 'failed'
              : request.kind === 'saved' && !request.recover && !asked
                ? 'offered'
                : 'queued',
          retryable:
            !request.superseded &&
            !(request.kind === 'saved' && request.interrupted),
          errorCode:
            request.kind === 'saved' && request.interrupted
              ? 'saved_review_interrupted'
              : undefined,
        },
      ];
    }
    if (next !== rows.current) commit(next);
  }, [requests, commit]);
  useEffect(() => {
    if (!historyFailed || !rows.current.some((row) => row.status === 'queued'))
      return;
    commit(
      rows.current.map((row) =>
        row.status === 'queued'
          ? {
              ...row,
              status: 'failed',
              errorCode: 'accepted_review_history',
              retryable: true,
            }
          : row
      )
    );
  }, [historyFailed, reviews, commit]);
  useEffect(() => {
    if (!ready || active.current) return;
    const next = rows.current.find(
      (row) => row.status === 'queued' && (row.kind !== 'saved' || row.ready)
    );
    if (!next) return;
    // Let composer, card and user-message effects take priority in this commit.
    const timer = setTimeout(() => {
      if (
        !alive.current ||
        !latest.current.ready ||
        active.current ||
        !rows.current.includes(next)
      )
        return;
      const token = {};
      active.current = token;
      commit(
        rows.current.map((row) =>
          row === next ? { ...row, status: 'checking' } : row
        )
      );
      void latest.current
        .send(next)
        .catch(() => ({ ok: false }) as SendResult)
        .then((result) => {
          if (!alive.current || active.current !== token) return;
          active.current = null;
          commit(
            rows.current.map((row) =>
              playReviewKey(row) !== playReviewKey(next)
                ? row
                : {
                    ...row,
                    status: row.superseded
                      ? 'superseded'
                      : result.ok
                        ? 'done'
                        : result.aborted
                          ? 'cancelled'
                          : 'failed',
                    retryable:
                      result.retryable === true ||
                      result.aborted === true ||
                      (result.errorCode === 'saved_review_stale' &&
                        row.kind === 'saved' &&
                        next.kind === 'saved' &&
                        row.version > next.version),
                    errorCode: result.errorCode,
                  }
            )
          );
        });
    }, 0);
    return () => clearTimeout(timer);
  }, [ready, reviews, commit]);
  const cancel = useCallback(
    (retryingProposalId?: string) => {
      if (!alive.current) return;
      active.current = null;
      commit(
        rows.current.map((row) =>
          ['queued', 'checking'].includes(row.status) ||
          playReviewKey(row) === retryingProposalId
            ? { ...row, status: 'cancelled' }
            : row
        )
      );
    },
    [commit]
  );
  const retry = useCallback(
    (proposalId: string) => {
      if (!alive.current) return;
      commit(
        rows.current.map((row) =>
          playReviewKey(row) !== proposalId
            ? row
            : row.status === 'offered' && row.kind === 'saved'
              ? // Asked for: it runs once the chat is free and no save is
                // still in flight.
                { ...row, status: 'queued', ready: !row.pending, asked: true }
              : row.retryable && ['failed', 'cancelled'].includes(row.status)
                ? { ...row, status: 'queued', errorCode: undefined }
                : row
        )
      );
    },
    [commit]
  );
  return { reviews, cancel, retry };
}
